"use client";

import * as React from "react";
import { Loader2, Printer, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Operation } from "@workspace/operations";
import { CheckOperation } from "@workspace/operations";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";

import {
	buildEscalationSummary,
	CANCEL_SUCCESS,
	describeCancelFailure,
	describeReprintFailure,
	describeRetryFailure,
	printJobStatusLabel,
	REPRINT_SUCCESS,
	RETRY_SUCCESS,
	type PrintJobFailure,
} from "../print-job-outcome";
import { printJobsApi } from "../print-state-api";
import type { PrintJobStateItem } from "../print-state-types";

type Action = "cancel" | "retry" | "reprint";

function statusVariant(status: string): "default" | "secondary" | "outline" {
	if (status === "Completed") return "default";
	if (status === "Failed" || status === "Expired") return "outline";
	return "secondary";
}

/**
 * HA-575 per-printer print state for an order, with the operator actions.
 *
 * Which controls appear is decided by the backend's affordance flags, not re-derived here — the
 * Staff Terminal and Management Panel render the same component so they cannot disagree about when
 * Cancel is still valid.
 */
export function OrderPrintStatePanel({
	tenant,
	venueId,
	orderId,
	operations,
	isDeviceOnline = true,
	onEscalate,
}: {
	tenant: string;
	venueId: string;
	orderId: string;
	operations: Operation[];
	/** Cancel and Retry need a live device; Reprint goes to the broker, which queues it (AC8). */
	isDeviceOnline?: boolean;
	/** Opens the contact path, pre-filled (AC11). Falls back to a copyable toast. */
	onEscalate?: (summary: string) => void;
}) {
	const queryClient = useQueryClient();
	const [confirmingCancel, setConfirmingCancel] = React.useState<string | null>(
		null,
	);
	const [pending, setPending] = React.useState<string | null>(null);

	const { data, isLoading, isError } = useQuery(
		printJobsApi.orderPrintStateOptions(tenant, venueId, orderId),
	);

	const act = useMutation(printJobsApi.printJobActionOptions(tenant, venueId));

	async function refresh() {
		await queryClient.invalidateQueries({
			queryKey: [tenant, venueId, "epos", "orders", orderId, "print-state"],
		});
	}

	function surfaceFailure(failure: PrintJobFailure, job: PrintJobStateItem) {
		// One recovery action per failure — the toast action IS that action.
		const action =
			failure.recovery === "refresh"
				? { label: "Refresh", onClick: () => void refresh() }
				: failure.recovery === "reprint"
					? { label: "Reprint", onClick: () => void run(job, "reprint") }
					: failure.recovery === "escalate"
						? { label: "Get help", onClick: () => escalate(job) }
						: {
								label: "Try again",
								onClick: () => void run(job, "retry"),
							};

		toast.error(failure.title, { description: failure.description, action });
	}

	function escalate(job: PrintJobStateItem) {
		const summary = buildEscalationSummary(job);
		if (onEscalate) {
			onEscalate(summary);
			return;
		}
		toast.warning("Printing needs attention", {
			description: summary,
			duration: 15_000,
		});
	}

	async function run(job: PrintJobStateItem, action: Action) {
		setPending(`${job.outboundJobId}:${action}`);
		try {
			const result = await act.mutateAsync({
				outboundJobId: job.outboundJobId,
				action,
			});

			if (result.success) {
				toast.success(
					action === "cancel"
						? CANCEL_SUCCESS
						: action === "retry"
							? RETRY_SUCCESS
							: REPRINT_SUCCESS,
				);
				await refresh();
				return;
			}

			const describe =
				action === "cancel"
					? describeCancelFailure
					: action === "retry"
						? describeRetryFailure
						: describeReprintFailure;

			surfaceFailure(describe(result.code, result.error), job);

			// A cancel that lost the race is a state-drift signal — pull the truth back in.
			if (action === "cancel") await refresh();
		} finally {
			setPending(null);
			setConfirmingCancel(null);
		}
	}

	if (isLoading) {
		return (
			<p className="text-muted-foreground text-sm">Loading print state…</p>
		);
	}

	if (isError) {
		return (
			<p className="text-muted-foreground text-sm">Print state unavailable.</p>
		);
	}

	if (!data || data.queues.length === 0) {
		return (
			<p className="text-muted-foreground text-sm">
				Nothing has been sent to a printer for this order.
			</p>
		);
	}

	return (
		<div className="space-y-3">
			{data.queues.map((queue) => (
				<div key={queue.queueId} className="space-y-1">
					<p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
						{queue.queueName}
					</p>
					<div className="divide-y rounded-md border">
						{queue.printers.map((job) => {
							const busy = pending?.startsWith(job.outboundJobId) ?? false;
							const isConfirming = confirmingCancel === job.outboundJobId;

							return (
								<div
									key={job.outboundJobId}
									className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5"
								>
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium">{job.printerId}</span>
										<Badge
											variant={statusVariant(job.status)}
											className="text-[10px] px-1.5 py-0"
										>
											{printJobStatusLabel(job.status)}
										</Badge>
										{job.attemptNumber > 1 && (
											<span className="text-muted-foreground text-[10px]">
												attempt {job.attemptNumber}
											</span>
										)}
										{job.errorMessage && (
											<span className="text-destructive text-xs">
												{job.errorMessage}
											</span>
										)}
									</div>

									<CheckOperation
										operation="venue-management-manage-print-jobs"
										operations={operations}
									>
										<div className="flex items-center gap-1">
											{job.canCancel &&
												(isConfirming ? (
													// Soft confirm — a single dismissible step (AC10).
													<>
														<Button
															size="sm"
															variant="destructive"
															className="h-7 text-xs"
															disabled={busy}
															onClick={() => void run(job, "cancel")}
														>
															{busy && (
																<Loader2 className="size-3 animate-spin" />
															)}
															Confirm cancel
														</Button>
														<Button
															size="sm"
															variant="ghost"
															className="h-7 text-xs"
															onClick={() => setConfirmingCancel(null)}
														>
															Keep
														</Button>
													</>
												) : (
													<Button
														size="sm"
														variant="ghost"
														className="h-7 text-xs"
														disabled={!isDeviceOnline || busy}
														title={
															isDeviceOnline ? undefined : "Device is offline"
														}
														onClick={() =>
															setConfirmingCancel(job.outboundJobId)
														}
													>
														<X className="size-3" />
														Cancel
													</Button>
												))}

											{job.canRetry && (
												<Button
													size="sm"
													variant="ghost"
													className="h-7 text-xs"
													disabled={!isDeviceOnline || busy}
													title={
														isDeviceOnline ? undefined : "Device is offline"
													}
													onClick={() => void run(job, "retry")}
												>
													{busy ? (
														<Loader2 className="size-3 animate-spin" />
													) : (
														<RotateCcw className="size-3" />
													)}
													Retry
												</Button>
											)}

											{job.shouldEscalate && (
												<Button
													size="sm"
													variant="outline"
													className="h-7 text-xs"
													onClick={() => escalate(job)}
												>
													Escalate
												</Button>
											)}

											{job.canReprint && (
												// No offline gate: this publishes to the broker, which
												// holds it until the device reconnects (AC8).
												<Button
													size="sm"
													variant="ghost"
													className="h-7 text-xs"
													disabled={busy}
													onClick={() => void run(job, "reprint")}
												>
													<Printer className="size-3" />
													Reprint
												</Button>
											)}
										</div>
									</CheckOperation>
								</div>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
}
